import { createApp } from "vue";
import App from "./App.vue";
import router from "./router";
import store from "./store";
import vuetify from "./plugins/vuetify";
import {
  getWavDownloadFromProxyWithId,
  getColorForString,
} from "./filters/utils.js";
import "@mdi/font/css/materialdesignicons.css";
import "./styles/tokens.css";
// import VueWaveSurfer from "vue-wave-surfer"; // Not Vue 3 compatible
// import VueSocketIOExt from "vue-socket.io-extended"; // Not Vue 3 compatible
// import VueSimpleAlert from "vue-simple-alert"; // Not Vue 3 compatible
import io from "socket.io-client";
import { initLiveSocket, refreshSocketAuth } from "@/services/liveSocket";
import {
  TOKEN_KEY,
  authRefusalAction,
  createTokenRefresher,
  wrapFetch,
} from "@/services/tokenRefresh";
import WaveformPlayer from "@/components/WaveformPlayer.vue";
import jamEngine from "@/audio/jamEngine";
import { configureClient } from "@/services/api/client.js";
import { markSessionExpired } from "@/shell/account.js";

// A 401 from any call through the shared API client drops the signed-in
// user, as the router guard would (SIGNED_IN_PLAN 3.4), and App.vue's
// watcher sends a page that needs an account to Log in and back (L11).
configureClient({
  onUnauthorized: () => {
    if (!store.state.loggedInUser) return;
    try {
      window.localStorage.removeItem(TOKEN_KEY);
    } catch (e) {
      /* storage unavailable */
    }
    markSessionExpired();
    store.commit("setLoggedInUser", null);
  },
});

// The JWT rides the handshake (re-read on every reconnect, so it's always the
// current login); the server acts on that verified identity, not on
// usernames in event payloads.
let handshakeToken = null; // the token the current socket connection carried
const socket = io(`${process.env.VUE_APP_API_URL}`, {
  auth: (cb) => {
    let token = null;
    try {
      token = window.localStorage.getItem(TOKEN_KEY);
    } catch (e) {
      /* storage unavailable: connect anonymously */
    }
    handshakeToken = token;
    cb(token ? { token } : {});
  },
});

// Token refresh (dev→prod migration window): any API response carrying
// X-CloudLoop-Token-Refresh replaces the stored JWT, once, and the socket
// re-handshakes with it. Installed before the app mounts so the router
// guard's first /api/auth/validate is already covered.
let tokenStorage = null;
try {
  tokenStorage = window.localStorage;
} catch (e) {
  /* storage unavailable: nothing to refresh */
}
const tokenRefresher = createTokenRefresher({
  storage: tokenStorage,
  key: TOKEN_KEY,
  onRefresh: () => refreshSocketAuth(),
});
if (typeof window.fetch === "function") {
  window.fetch = wrapFetch(window.fetch.bind(window), {
    apiBase: process.env.VUE_APP_API_URL,
    refresher: tokenRefresher,
  });
}
io.protocol = 5;

const app = createApp(App);

// Global components (Vue 3 syntax)
app.component("WaveformPlayer", WaveformPlayer);

// Global properties (Vue 3 filters replacement)
app.config.globalProperties.$filters = {
  getWavDownloadFromProxy: getWavDownloadFromProxyWithId,
  getColorForString: getColorForString,
};

// Socket.io configuration for Vue 3
app.config.globalProperties.$socket = socket;
app.provide("socket", socket);

// Live session updates: re-auth + re-join the current room on every
// (re)connect, acked joins, presence heartbeats (see services/liveSocket.js).
initLiveSocket(socket, () => store.state.loggedInUser, {
  // Fresh token for the one this handshake carried; ignored if a login,
  // logout or an HTTP refresh already replaced it.
  onTokenRefresh: (token) => tokenRefresher.apply(token, handshakeToken),
  // The server refused the handshake: the stored token will never verify
  // again (or strict mode wants one). Drop it and prompt a re-login.
  onAuthRefused: (kind) => {
    const hadToken = !!tokenRefresher.current();
    const action = authRefusalAction(kind, { hadToken });
    if (action.clearToken) {
      try {
        window.localStorage.removeItem(TOKEN_KEY);
      } catch (e) {
        /* storage unavailable */
      }
    }
    const wasSignedIn = store.state.loggedInUser !== null;
    const route = router.currentRoute.value;
    const publicRoute = route.meta && route.meta.requiresAuth === false;
    // A stale token on a public page (share link, public session): keep
    // listening anonymously; the login prompt is for pages that need it.
    const relogin =
      action.relogin && (kind === "auth-required" || !publicRoute);
    const signsOut = action.clearToken || kind === "auth-required";
    // Signed in on a page that needs an account: App.vue's watcher takes
    // it to Log in and back (its own replace would otherwise win the race
    // with a push from here).
    const watcherGoes = signsOut && wasSignedIn && !publicRoute;
    if (signsOut) {
      if (watcherGoes && relogin) markSessionExpired();
      // Signed out → App.vue's loggedInUser watcher re-handshakes.
      store.commit("setLoggedInUser", null);
    }
    if (relogin && route.name !== "Login" && !watcherGoes) {
      router.push({ name: "Login", query: { redirect: route.fullPath } });
    }
    if (action.reconnect && !wasSignedIn) socket.connect();
  },
});

// Plugins
app.use(router);
app.use(store);
app.use(vuetify);
// app.use(VueWaveSurfer); // Not Vue 3 compatible
// app.use(VueSocketIOExt, socket); // Not Vue 3 compatible
// app.use(VueSimpleAlert); // Not Vue 3 compatible

app.mount("#app");

// Warm the Web Audio context on the first touch anywhere, so iOS has already
// unlocked it by the time "Play all" is tapped. See jamEngine.arm().
jamEngine.arm();
